import { ArrowRight, Clapperboard, Link as LinkIcon, Loader2, Upload, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SiDropbox, SiGoogledrive, SiInstagram, SiTwitch, SiYoutube } from 'react-icons/si';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ProBadge } from '@/components/ProBadge';
import { cn } from '@/lib/utils';
import type { CommunityPreviewAvatar } from '@/lib/library/pickCommunityPreviewAvatars';
import { LIBRARY_URL_SOURCES } from '@/lib/library/resolveLibrarySourceAccess';

interface LibraryCreateHeroProps {
  videoUrl: string;
  isLoadingMetadata: boolean;
  isFirstClip: boolean;
  compact: boolean;
  copyPrefix?: 'library' | 'projects';
  communityVariant?: 'card' | 'quiet';
  headingId?: string;
  communityAvatars?: CommunityPreviewAvatar[];
  communityChannelCount?: number;
  onVideoUrlChange: (value: string) => void;
  onGenerate: () => void;
  onUploadFile: () => void;
  children?: ReactNode;
}

const SOURCE_ICONS = {
  youtube: SiYoutube,
  dropbox: SiDropbox,
  frameio: Clapperboard,
  mediasilo: Clapperboard,
  instagram: SiInstagram,
  twitch: SiTwitch,
  'google-drive': SiGoogledrive,
} as const;

const STEPS = [
  { titleKey: 'library.steps.addVideo.title', descriptionKey: 'library.steps.addVideo.description' },
  { titleKey: 'library.steps.aiSelects.title', descriptionKey: 'library.steps.aiSelects.description' },
  { titleKey: 'library.steps.export.title', descriptionKey: 'library.steps.export.description' },
] as const;

const LibraryCreateHero = ({
  videoUrl,
  isLoadingMetadata,
  isFirstClip,
  compact,
  copyPrefix = 'library',
  communityVariant = 'card',
  headingId,
  communityAvatars = [],
  communityChannelCount = 0,
  onVideoUrlChange,
  onGenerate,
  onUploadFile,
  children,
}: LibraryCreateHeroProps) => {
  const { t } = useTranslation();
  const isQuietCommunity = communityVariant === 'quiet';

  // Returning users: one row (link, Gerar cortes, Arquivo) so the list starts
  // right below it. The steps, the Dropbox note and the big title are for
  // the first visit only; on a phone the old compact hero filled the screen.
  if (compact) {
    return (
      <section data-testid="library-create-hero" data-compact="true" className="relative w-full">
        <h1 id={headingId} className="sr-only">
          {t(`${copyPrefix}.heroTitle`)} {t(`${copyPrefix}.heroTitleAccent`)}
        </h1>
        <div
          id="library-add-video"
          className="library-hero-in flex flex-col gap-2 rounded-2xl border border-white/5 bg-card/80 p-2.5 shadow-[0_24px_80px_-32px_hsl(var(--primary)/0.35)] sm:flex-row sm:items-center"
        >
          <div className="relative min-w-0 flex-1">
            <div
              data-testid="library-url-input-decoration"
              aria-hidden="true"
              className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2"
            >
              <LinkIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <Input
              id="video-url-input"
              type="url"
              inputMode="url"
              autoComplete="url"
              placeholder={t('library.urlPlaceholder')}
              value={videoUrl}
              onChange={(event) => onVideoUrlChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') onGenerate();
              }}
              className="h-11 rounded-full border-border/80 bg-background/80 pl-11 pr-4 text-sm"
            />
          </div>
          <div data-testid="library-hero-actions" className="flex flex-row gap-2">
            <Button
              type="button"
              variant="default"
              size="lg"
              className="h-11 min-w-0 flex-1 rounded-full px-5 text-sm sm:flex-none"
              onClick={onGenerate}
              disabled={isLoadingMetadata}
            >
              {isLoadingMetadata ? t('projects.analyzingVideo') : t('library.generateClips')}
              {isLoadingMetadata ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              id="video-file-input"
              aria-label={t('library.uploadFile')}
              className="h-11 min-w-0 flex-1 gap-1.5 rounded-full border-primary/50 bg-primary/[0.08] px-4 text-sm text-foreground hover:bg-primary/15 hover:text-foreground sm:flex-none"
              onClick={onUploadFile}
            >
              <Upload className="h-4 w-4" />
              <span className="truncate">{t('library.uploadFileShort')}</span>
              <ProBadge className="px-1.5 py-0 text-[9px] leading-4" />
            </Button>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-1 text-xs text-muted-foreground">
          <span data-testid="library-url-sources-compact" className="hidden sm:inline">
            {LIBRARY_URL_SOURCES.map((source) => t(source.labelKey)).join(' · ')}
          </span>
          <div className="flex items-center gap-3">
            {children}
            <Link
              id="library-explore-community"
              to="/community-library"
              data-community-variant={communityVariant}
              className="inline-flex items-center gap-1.5 font-medium text-foreground/90 underline-offset-4 transition-colors hover:text-foreground hover:underline"
            >
              <Users className="h-3.5 w-3.5" />
              {t(`${copyPrefix}.exploreCommunity`)}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="library-create-hero"
      data-compact="false"
      className="relative mx-auto w-full max-w-3xl text-center space-y-5 lg:space-y-6"
    >
      <div className="library-hero-in space-y-2 sm:space-y-3">
        <h1
          id={headingId}
          className="font-bold tracking-tight text-balance text-foreground text-2xl sm:text-3xl lg:text-[2.15rem] lg:leading-[1.15]"
        >
          <span className="block">{t(`${copyPrefix}.heroTitle`)}</span>
          <span className="block text-primary">{t(`${copyPrefix}.heroTitleAccent`)}</span>
        </h1>
        <p className="mx-auto max-w-xl text-muted-foreground text-pretty text-sm sm:text-base">
          {t(`${copyPrefix}.heroSubtitle`)}
        </p>
      </div>

      <div
        id="library-add-video"
        className="library-hero-in rounded-2xl border border-white/5 bg-card/80 shadow-[0_24px_80px_-32px_hsl(var(--primary)/0.35)] p-3.5 sm:p-5"
        style={{ animationDelay: '80ms' }}
      >
        <div className="relative">
          <div
            data-testid="library-url-input-decoration"
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2"
          >
            <LinkIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <Input
            id="video-url-input"
            type="url"
            inputMode="url"
            autoComplete="url"
            placeholder={t('library.urlPlaceholder')}
            value={videoUrl}
            onChange={(event) => onVideoUrlChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onGenerate();
            }}
            className="rounded-full border-border/80 bg-background/80 pl-11 pr-4 text-sm h-11 sm:h-12 sm:text-base"
          />
        </div>

        <div
          data-testid="library-url-sources"
          className="flex flex-wrap items-center justify-center mt-2.5 gap-1.5 sm:gap-2"
        >
          {LIBRARY_URL_SOURCES.map((source) => {
            const Icon = SOURCE_ICONS[source.id];
            return (
              <span
                key={source.id}
                data-testid={`library-source-${source.id}`}
                className="inline-flex items-center rounded-full border border-border/70 bg-background/70 font-medium text-foreground/90 gap-1.5 px-2.5 py-1 text-[11px]"
              >
                <Icon className="h-3 w-3" aria-hidden="true" />
                {t(source.labelKey)}
                {source.pro ? <ProBadge className="px-1.5 py-0 text-[9px] leading-4" /> : null}
              </span>
            );
          })}
        </div>

        <div
          data-testid="library-hero-actions"
          className="flex flex-row mt-3 gap-2 sm:gap-2.5"
        >
          <Button
            type="button"
            variant="default"
            size="lg"
            className="min-w-0 flex-1 rounded-full px-3 text-sm sm:px-5 h-11 sm:h-12 sm:text-base"
            onClick={onGenerate}
            disabled={isLoadingMetadata}
          >
            {isLoadingMetadata ? t('projects.analyzingVideo') : isFirstClip ? t('library.generateFirstClip') : t('library.generateClips')}
            {isLoadingMetadata ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="lg"
            id="video-file-input"
            aria-label={t('library.uploadFile')}
            className="min-w-0 gap-1.5 rounded-full border-primary/50 bg-primary/[0.08] px-2.5 text-sm text-foreground hover:bg-primary/15 hover:text-foreground sm:gap-2 sm:px-5 h-11 flex-1 sm:h-12 sm:text-base"
            onClick={onUploadFile}
          >
            <Upload className="h-4 w-4" />
            <span className="truncate sm:hidden">{t('library.uploadFileShort')}</span>
            <span className="hidden truncate sm:inline">{t('library.uploadFile')}</span>
            <ProBadge className="px-1.5 py-0 text-[9px] leading-4" />
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">{t('dropbox.linkHelp')}</p>

      <ol
        id="library-hero-steps"
        className="library-hero-in relative grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6"
        style={{ animationDelay: '160ms' }}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-[16%] right-[16%] hidden border-t border-dashed border-border/70 sm:block top-5"
        />
        {STEPS.map((step, index) => (
          <li
            key={step.titleKey}
            className="relative z-10 flex items-center justify-center text-center sm:flex-col gap-3"
          >
            <span className="flex shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-background font-semibold text-primary h-10 w-10 text-sm">
              {index + 1}
            </span>
            <div className="min-w-0 space-y-1 sm:pt-1">
              <p className="font-semibold text-foreground text-sm">{t(step.titleKey)}</p>
              <p className="text-xs text-muted-foreground text-pretty">{t(step.descriptionKey)}</p>
            </div>
          </li>
        ))}
      </ol>

      {isQuietCommunity ? (
        <Link
          id="library-explore-community"
          to="/community-library"
          data-community-variant="quiet"
          className="library-hero-in text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
          style={{ animationDelay: '220ms' }}
        >
          {t(`${copyPrefix}.exploreCommunity`)}
        </Link>
      ) : (
      <Link
        id="library-explore-community"
        to="/community-library"
        data-community-variant="card"
        className="library-hero-in group relative flex items-center overflow-hidden rounded-2xl border border-primary/30 bg-card/80 text-left shadow-[0_20px_70px_-32px_hsl(var(--primary)/0.55)] transition-colors hover:border-primary/60 hover:bg-card gap-3 p-3.5 sm:gap-4 sm:p-4"
        style={{ animationDelay: '220ms' }}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_left,hsl(var(--primary)/0.18),transparent_58%)]"
        />
        {communityAvatars.length > 0 ? (
          <div data-testid="library-community-avatars" aria-hidden="true" className="relative z-10 flex shrink-0 items-center">
            {communityAvatars.map((avatar, index) => (
              avatar.thumbnail ? (
                <img
                  key={avatar.id}
                  src={avatar.thumbnail}
                  alt={avatar.name}
                  title={avatar.name}
                  className={cn(
                    'rounded-full object-cover ring-2 ring-background',
                    'h-10 w-10',
                    index > 0 && '-ml-2',
                  )}
                  style={{ zIndex: communityAvatars.length - index }}
                />
              ) : (
                <span
                  key={avatar.id}
                  title={avatar.name}
                  className={cn(
                    'flex items-center justify-center rounded-full bg-secondary text-[11px] font-semibold text-foreground ring-2 ring-background',
                    'h-10 w-10',
                    index > 0 && '-ml-2',
                  )}
                  style={{ zIndex: communityAvatars.length - index }}
                >
                  {avatar.name.charAt(0).toUpperCase()}
                </span>
              )
            ))}
          </div>
        ) : (
          <span
            data-testid="library-community-avatars"
            aria-hidden="true"
            className="relative z-10 flex shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary ring-1 ring-primary/30 h-10 w-10"
          >
            <Users className="h-5 w-5" />
          </span>
        )}
        <div className="relative z-10 min-w-0 flex-1 space-y-0.5">
          <p className="font-semibold text-foreground text-sm sm:text-base">
            {t('library.exploreCommunity')}
          </p>
          <p className="text-xs text-muted-foreground text-pretty sm:text-sm">
            {t('library.exploreCommunityDescription')}
          </p>
          {communityChannelCount > 0 && (
            <p className="text-xs font-medium text-primary">
              {t('library.exploreCommunityChannels', { count: communityChannelCount })}
            </p>
          )}
        </div>
        <span aria-hidden="true" className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform group-hover:translate-x-0.5">
          <ArrowRight className="h-4 w-4" />
        </span>
      </Link>
      )}
      {children}
    </section>
  );
};

export default LibraryCreateHero;
